import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  LayoutDashboard,
  Briefcase,
  GitBranch,
  BarChart3,
  Users,
  Settings,
  LogOut,
  ChevronDown,
  MessageSquare,
  Wallet
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_africa-ventures/artifacts/mjqy16nm_image.png";

const navItems = [
  { path: '/app/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'team_member', 'lp'] },
  { path: '/app/portfolio', label: 'Portfolio', icon: Briefcase, roles: ['admin', 'team_member'] },
  { path: '/app/pipeline', label: 'Deal Pipeline', icon: GitBranch, roles: ['admin', 'team_member'] },
  { path: '/app/analytics', label: 'Analytics', icon: BarChart3, roles: ['admin', 'team_member', 'lp'] },
  { path: '/app/lp-dashboard', label: 'LP Dashboard', icon: Wallet, roles: ['admin', 'lp'] },
  { path: '/app/inquiries', label: 'Inquiries', icon: MessageSquare, roles: ['admin'] },
  { path: '/app/team', label: 'Team', icon: Users, roles: ['admin'] },
  { path: '/app/settings', label: 'Settings', icon: Settings, roles: ['admin', 'team_member', 'lp'] },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  };

  const filteredNavItems = navItems.filter(item => 
    item.roles.includes(user?.role)
  );

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-[#1E3A5F] flex flex-col fixed h-full" data-testid="sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <div className="bg-white rounded-lg p-2 inline-block">
            <img 
              src={LOGO_URL} 
              alt="Berkshire Africa" 
              className="h-8"
              data-testid="sidebar-logo"
            />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1" data-testid="sidebar-nav">
          {filteredNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'active' : ''}`
              }
              data-testid={`nav-${item.path.slice(1)}`}
            >
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* User Menu */}
        <div className="p-4 border-t border-white/10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                className="w-full justify-start text-white hover:bg-white/10 hover:text-white"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-8 w-8 mr-3">
                  <AvatarFallback className="bg-white/20 text-white text-xs">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-slate-300 capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
                <ChevronDown className="h-4 w-4 text-slate-300" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/app/settings')} data-testid="menu-settings">
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64" data-testid="main-content">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
